// features/vehicles/VehiclePage.tsx
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Page,
    IndexTable,
    Card,
    Button,
    IndexFilters,
    useIndexResourceState,
    Text,
    useBreakpoints,
    IndexFiltersMode,
    useSetIndexFiltersMode,
    type IndexFiltersProps,
    type TabProps,
    ChoiceList,
    AppProvider,
    FormLayout,
    TextField,
    Banner,
    InlineError,
    ButtonGroup,
    Checkbox
} from '@shopify/polaris';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import type { Vehicle, VehicleCreate, VehicleEdit } from '../../app/models/Vehicle';
import { MiniBanner } from '../components/MiniBanner';
import { DeleteIcon } from '@shopify/polaris-icons';
import enTranslations from '@shopify/polaris/locales/en.json';

// Satisfy Polaris' {[key:string]: unknown} constraint
type VehicleRow = Vehicle & { [key: string]: unknown };

type FieldErrors = {
    yearFrom?: string;
    yearTo?: string;
    make?: string;
    model?: string;
};

const DEBOUNCE_MS = 500;
const YEAR_MIN = 1886;
const YEAR_MAX = 2100;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_MAX_DYNAMIC = CURRENT_YEAR + 1; // allow next model year

const isFourDigits = (s: string) => /^\d{4}$/.test(s);

const inYearBounds = (y: number) =>
    y >= YEAR_MIN && y <= YEAR_MAX_DYNAMIC;


declare global {
    interface Window {
        shopify?: { toast?: { show?: (msg: string | { message: string; duration?: number }) => void } };
    }
}
const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);


const VehiclesPage: React.FC = () => {
    const { vehicleStore } = useStore();
    const { vehicles, loadVehicles, page, hasNext, totalCount, loadingInitial } = vehicleStore;

    // ---------- Modal open/close ----------
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingIsCustom, setEditingIsCustom] = useState(false);
    const [persistFitments, setPersistFitments] = useState(true);
    const openAdd = useCallback(() => {
        setEditingId(null);
        setEditingIsCustom(false);
        setPersistFitments(true);
        setModalOpen(true);
    }, []);
    const onEditClick = useCallback((v: Vehicle) => {
        setEditingId(v.id);
        setEditingIsCustom(!!v.isCustom);
        setYearFrom(v.field1);
        setYearTo(v.field1);
        setMake(v.field2);
        setModel(v.field3);
        setSubmodel(v.field4);
        setPersistFitments(true);
        setErrors({});
        setSubmitError(undefined);
        setModalOpen(true);
    }, []);

    const copyText = useCallback(async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast('Copied');
        } catch {
            showToast('Copy failed');
        }
    }, []);


    // ---------- Suggestions (replace with store/API as needed) ----------
    const makeSuggestions = useMemo(
        () => ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Nissan', 'Hyundai', 'Kia'],
        [],
    );
    const modelSuggestions = useMemo(
        () => ['Camry', 'Civic', 'F-150', 'Silverado', '3 Series', 'C-Class', 'Golf', 'Altima', 'Elantra', 'Sorento'],
        [],
    );
    const submodelSuggestions = useMemo(
        () => ['Base', 'SE', 'LE', 'XLE', 'Sport', 'Limited', 'Touring', 'Premium'],
        [],
    );

    // ---------- Form state (modal) ----------
    const [yearFrom, setYearFrom] = useState('');
    const [yearTo, setYearTo] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [submodel, setSubmodel] = useState('');

    const [makeSuggestion, setMakeSuggestion] = useState<string | undefined>();
    const [modelSuggestion, setModelSuggestion] = useState<string | undefined>();
    const [submodelSuggestion, setSubmodelSuggestion] = useState<string | undefined>();

    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);
    const [lastAdded, setLastAdded] = useState<VehicleCreate | null>(null);
    const [lastAddedExists, setLastAddedExists] = useState(false);

    const clampDigits = (val: string) => val.replace(/[^\d]/g, '').slice(0, 4);
    const findSuggestion = (val: string, list: string[]) =>
        val ? list.find(s => s.toLowerCase().startsWith(val.toLowerCase())) : undefined;

    const onYearFromChange = useCallback((v: string) => {
        const val = clampDigits(v);
        setYearFrom(val);
        setErrors(prev => ({ ...prev, yearFrom: undefined })); // clear field error on edit
    }, []);

    const onYearToChange = useCallback((v: string) => {
        const val = clampDigits(v);
        setYearTo(val);
        setErrors(prev => ({ ...prev, yearTo: undefined }));
    }, []);

    const onMakeChange = useCallback((v: string) => { setMake(v); setMakeSuggestion(findSuggestion(v, makeSuggestions)); }, [makeSuggestions]);
    const onModelChange = useCallback((v: string) => { setModel(v); setModelSuggestion(findSuggestion(v, modelSuggestions)); }, [modelSuggestions]);
    const onSubmodelChange = useCallback((v: string) => { setSubmodel(v); setSubmodelSuggestion(findSuggestion(v, submodelSuggestions)); }, [submodelSuggestions]);

    const acceptSuggestion = (current: string, suggestion?: string) =>
        suggestion && suggestion.toLowerCase().startsWith(current.toLowerCase()) ? suggestion : current;

    const onKeyDownApplySuggestion = useCallback(
        (setter: (v: string) => void, current: string, suggestion?: string) =>
            (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === 'Tab') setter(acceptSuggestion(current, suggestion));
            },
        [],
    );

    const validate = (): boolean => {
        const e: FieldErrors = {};

        // Required fields
        if (!yearFrom.trim()) e.yearFrom = 'Start year is required';
        if (!make.trim()) e.make = 'Make is required';
        if (!model.trim()) e.model = 'Model is required';

        // Parse ints only if present
        const hasFrom = !!yearFrom;
        const hasTo = !!yearTo;
        const yf = hasFrom && isFourDigits(yearFrom) ? parseInt(yearFrom, 10) : NaN;
        const yt = hasTo && isFourDigits(yearTo) ? parseInt(yearTo, 10) : NaN;

        // Format checks (4 digits)
        if (hasFrom && !isFourDigits(yearFrom)) {
            e.yearFrom = 'Enter a 4-digit year (e.g., 2018)';
        }
        if (hasTo && !isFourDigits(yearTo)) {
            e.yearTo = 'Enter a 4-digit year (e.g., 2024)';
        }

        // Bounds (only if 4-digit parse is OK)
        if (!e.yearFrom && hasFrom && (Number.isNaN(yf) || !inYearBounds(yf))) {
            e.yearFrom = `Year must be between ${YEAR_MIN} and ${YEAR_MAX_DYNAMIC}`;
        }
        if (!e.yearTo && hasTo && (Number.isNaN(yt) || !inYearBounds(yt))) {
            e.yearTo = `Year must be between ${YEAR_MIN} and ${YEAR_MAX_DYNAMIC}`;
        }

        // Relationship: if yearTo present, yearFrom must be present
        if (!e.yearTo && hasTo && !hasFrom) {
            e.yearTo = 'Provide a start year first';
        }

        // Relationship: from <= to (only if both valid so far)
        if (!e.yearFrom && !e.yearTo && hasFrom && hasTo && yf > yt) {
            e.yearTo = 'End year must be greater than or equal to start year';
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };


    const resetForm = useCallback(() => {
        setYearFrom(''); setYearTo(''); setMake(''); setModel(''); setSubmodel('');
        setMakeSuggestion(undefined); setModelSuggestion(undefined); setSubmodelSuggestion(undefined);
        setErrors({}); setSubmitError(undefined);
    }, []);

    const handleClose = useCallback(() => {
        resetForm();
        setLastAdded(null);
        setLastAddedExists(false);
        setEditingId(null);
        setEditingIsCustom(false);
        setPersistFitments(true);
        setModalOpen(false);
    }, [resetForm]);

    const onCopyLast = useCallback(() => {
        if (!lastAdded) return;
        setYearFrom(lastAdded.yearFrom.toString());
        setYearTo(lastAdded.yearTo.toString());
        setMake(lastAdded.make);
        setModel(lastAdded.model);
        setSubmodel(lastAdded.submodel || '');
        setLastAdded(null);
        setLastAddedExists(false);
    }, [lastAdded]);

    const lastAddedLabel = useMemo(() => {
        if (!lastAdded) return '';
        const range = lastAdded.yearFrom === lastAdded.yearTo
            ? `${lastAdded.yearFrom}`
            : `${lastAdded.yearFrom}-${lastAdded.yearTo}`;
        return `${lastAdded.make}, ${lastAdded.model}${lastAdded.submodel ? `, ${lastAdded.submodel}` : ''}, ${range}`;
    }, [lastAdded]);


    const onSave = useCallback(async () => {
        setSubmitError(undefined);
        if (!validate()) return;

        setSubmitting(true);
        const normalizedYearTo = yearTo || yearFrom;

        const basePayload: VehicleCreate = {
            yearFrom: parseInt(yearFrom, 10),
            yearTo: parseInt(normalizedYearTo, 10),
            make: make.trim(),
            model: model.trim(),
            submodel: submodel.trim() || null,
        };

        try {
            if (editingId) {
                const payload: VehicleEdit = { ...basePayload, isCustom: editingIsCustom, persistFitments };
                await vehicleStore.update(editingId, payload);
                await loadVehicles(1);
                showToast('Vehicle saved');
            } else {
                await vehicleStore.create(basePayload);
                await loadVehicles(1);

                showToast('Vehicle added');
                setLastAdded(basePayload);
                setLastAddedExists(false);
                resetForm();
            }
        } catch (err: any) {
            if (!editingId && err?.response?.status === 409) {
                await loadVehicles(1);
                showToast('Vehicle already exists');
                setLastAdded(basePayload);
                setLastAddedExists(true);
                resetForm();
            } else {
                setSubmitError(err?.message ?? (editingId ? 'Failed to save vehicle' : 'Failed to create vehicle'));
            }
        } finally {
            setSubmitting(false);
        }
    }, [yearFrom, yearTo, make, model, submodel, editingId, editingIsCustom, persistFitments, vehicleStore, loadVehicles, resetForm]);



    // ---------- Existing list logic ----------
    const [queryValue, setQueryValue] = useState('');
    const [selectedSource, setSelectedSource] = useState<string[]>([]);
    const [sortSelected, setSortSelected] = useState<string[]>(['year desc']);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const didInitialLoadRef = useRef(false);

    useEffect(() => {
        loadVehicles();
        didInitialLoadRef.current = true;
    }, [loadVehicles]);

    useEffect(() => {
        if (!didInitialLoadRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            loadVehicles(1, queryValue, selectedSource[0] || '', sortSelected[0]);
        }, DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [queryValue, selectedSource, sortSelected, loadVehicles]);

    const handleQueryChange = useCallback((v: string) => setQueryValue(v), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);
    const onSourceChange = useCallback((v: string[]) => setSelectedSource(v), []);
    const sortOptions: IndexFiltersProps['sortOptions'] = [
        { label: 'Year', value: 'year asc', directionLabel: 'Ascending' },
        { label: 'Year', value: 'year desc', directionLabel: 'Descending' },
        { label: 'Make', value: 'make asc', directionLabel: 'A-Z' },
        { label: 'Make', value: 'make desc', directionLabel: 'Z-A' },
        { label: 'Assigned products', value: 'assigned asc', directionLabel: 'Lowest to highest' },
        { label: 'Assigned products', value: 'assigned desc', directionLabel: 'Highest to lowest' },
    ];
    const handleSort = useCallback((v: string[]) => setSortSelected(v), []);
    const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);
    const onClearAll = useCallback(() => {
        setQueryValue(''); setSelectedSource([]); setSortSelected(['year desc']);
    }, []);
    const tabs: TabProps[] = [{ id: 'all', content: 'All' }];
    const [selected, setSelected] = useState(0);

    const hasPrevious = page > 1;
    const onNext = useCallback(() => { if (hasNext) loadVehicles(page + 1, queryValue, selectedSource[0] || '', sortSelected[0]); },
        [hasNext, loadVehicles, page, queryValue, selectedSource, sortSelected]);
    const onPrevious = useCallback(() => { if (hasPrevious) loadVehicles(page - 1, queryValue, selectedSource[0] || '', sortSelected[0]); },
        [hasPrevious, loadVehicles, page, queryValue, selectedSource, sortSelected]);

    const rows: VehicleRow[] = (Array.isArray(vehicles) ? vehicles.slice() : []) as VehicleRow[];
    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState<VehicleRow>(rows, { resourceIDResolver: (v) => String(v.id) });
    const isCondensed = useBreakpoints().smDown;

    // ---------- BULK ACTIONS (Delete modal + Merge) ----------
    const selectedCount = allResourcesSelected ? totalCount : selectedResources.length;

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState<{ hasCustom: boolean; hasStandard: boolean; customCount: number }>({
        hasCustom: false,
        hasStandard: false,
        customCount: 0,
    });

    const onBulkMerge = useCallback(async () => {
        const count = allResourcesSelected ? totalCount : selectedResources.length;
        if (count < 2) {
            showToast('Select 2 or more vehicles to merge');
            return;
        }

        const defaultTarget = !allResourcesSelected && selectedResources.length > 0 ? selectedResources[0] : '';
        const targetInput = window.prompt('Enter target Vehicle ID to keep (others will be merged into this):', defaultTarget || '');
        const targetId = targetInput ? Number(targetInput) : NaN;
        if (!targetId || Number.isNaN(targetId)) return;

        try {
            if (typeof (vehicleStore as any).mergeVehicles === 'function') {
                await (vehicleStore as any).mergeVehicles({
                    targetId,
                    ids: allResourcesSelected ? null : selectedResources.map((id) => Number(id)),
                    scope: allResourcesSelected ? {
                        query: queryValue,
                        source: selectedSource[0] || '',
                        sort: sortSelected[0],
                    } : undefined,
                });
            } else {
                showToast('Merge not implemented');
                return;
            }

            showToast('Vehicles merged');
            clearSelection();
            await loadVehicles(1, queryValue, selectedSource[0] || '', sortSelected[0]);
        } catch (e: any) {
            showToast(e?.message ?? 'Merge failed');
        }
    }, [allResourcesSelected, totalCount, selectedResources, vehicleStore, clearSelection, loadVehicles, queryValue, selectedSource, sortSelected]);

    const onBulkDeleteClick = useCallback(() => {
        const count = allResourcesSelected ? totalCount : selectedResources.length;
        if (count < 1) return;

        if (allResourcesSelected) {
            const hasStandard = selectedSource[0] !== 'Custom';
            const hasCustom = selectedSource[0] !== 'System';
            setDeleteInfo({ hasStandard, hasCustom, customCount: hasCustom ? count : 0 });
        } else {
            const selectedVehicles = rows.filter((v) => selectedResources.includes(String(v.id)));
            const customVehicles = selectedVehicles.filter((v) => v.isCustom);
            setDeleteInfo({
                hasStandard: selectedVehicles.length > customVehicles.length,
                hasCustom: customVehicles.length > 0,
                customCount: customVehicles.length,
            });
        }

        setDeleteOpen(true);
    }, [allResourcesSelected, totalCount, selectedResources, rows, selectedSource]);

    const confirmDelete = useCallback(async () => {
        setDeleting(true);
        try {
            if (typeof (vehicleStore as any).bulkDelete === 'function') {
                if (allResourcesSelected) {
                    await (vehicleStore as any).bulkDelete({
                        ids: null,
                        scope: {
                            query: queryValue,
                            source: 'Custom',
                            sort: sortSelected[0],
                        },
                    });
                } else {
                    const ids = rows
                        .filter((v) => selectedResources.includes(String(v.id)) && v.isCustom)
                        .map((v) => Number(v.id));
                    await (vehicleStore as any).bulkDelete({ ids });
                }
            } else {
                const ids = allResourcesSelected
                    ? []
                    : rows.filter((v) => selectedResources.includes(String(v.id)) && v.isCustom).map((v) => Number(v.id));
                await Promise.all(ids.map((id) => (vehicleStore as any).delete?.(id)));
            }
            showToast('Vehicles deleted');
            setDeleteOpen(false);
            clearSelection();
            await loadVehicles(1, queryValue, selectedSource[0] || '', sortSelected[0]);
        } catch (e: any) {
            showToast(e?.message ?? 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    }, [allResourcesSelected, selectedResources, rows, vehicleStore, clearSelection, loadVehicles, queryValue, selectedSource, sortSelected]);

    const promotedBulkActions = [
        { content: 'Merge', onAction: onBulkMerge, disabled: selectedCount < 2 },
    ];
    const bulkActions = [
        { icon: DeleteIcon, content: 'Delete', destructive: true, onAction: onBulkDeleteClick, disabled: selectedCount < 1 },
    ];

    return (
        <>
            {/* App Bridge React Modal with local Polaris AppProvider so portal components work */}
            <Modal id="add-vehicle" open={modalOpen} onHide={handleClose}>
                <AppProvider i18n={enTranslations}>
                    <div style={{ padding: 16 }} onInput={() => { if (lastAdded) { setLastAdded(null); setLastAddedExists(false); } }}>
                        {submitError && (
                            <Banner tone="critical">{submitError}</Banner>
                        )}

                        {editingId && !editingIsCustom && (
                            <div style={{ backgroundColor: '#f6f6f7', padding: 12, marginTop: 8, marginBottom: 16 }}>
                                <Text as="p">This is a system vehicle and cannot be edited. Saving will create a new vehicle.</Text>
                                <Checkbox
                                    label="Persist saved fitments"
                                    checked={persistFitments}
                                    onChange={(v: boolean) => setPersistFitments(v)}
                                />
                            </div>
                        )}

                        <FormLayout>
                            {!editingId && lastAdded && (
                                <FormLayout.Group>
                                    <MiniBanner
                                        tone={lastAddedExists ? 'info' : 'success'}
                                        title={lastAddedExists ? 'Vehicle already exists' : 'Vehicle added'}
                                        onDismiss={() => { setLastAdded(null); setLastAddedExists(false); }}
                                        actions={[
                                            { content: 'Duplicate', onAction: onCopyLast },
                                        ]}
                                    >
                                        <b>{lastAddedLabel}</b>
                                    </MiniBanner>

                                </FormLayout.Group>
                            )}

                            <FormLayout.Group condensed>
                                <TextField
                                    label="Year (from)"
                                    value={yearFrom}
                                    onChange={onYearFromChange}
                                    placeholder="e.g. 2010"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    error={errors.yearFrom}
                                />
                                <TextField
                                    label="Year (to)"
                                    value={yearTo}
                                    onChange={onYearToChange}
                                    placeholder="e.g. 2018"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    error={errors.yearTo}
                                />
                            </FormLayout.Group>

                            <div onKeyDown={onKeyDownApplySuggestion(setMake, make, makeSuggestion)}>
                                <TextField
                                    label="Make"
                                    value={make}
                                    onChange={onMakeChange}
                                    autoComplete="off"
                                    placeholder="e.g. Toyota"
                                    suggestion={makeSuggestion}
                                    error={errors.make}
                                />
                            </div>

                            <div onKeyDown={onKeyDownApplySuggestion(setModel, model, modelSuggestion)}>
                                <TextField
                                    label="Model"
                                    value={model}
                                    onChange={onModelChange}
                                    autoComplete="off"
                                    placeholder="e.g. Camry"
                                    suggestion={modelSuggestion}
                                    error={errors.model}
                                />
                            </div>

                            <div onKeyDown={onKeyDownApplySuggestion(setSubmodel, submodel, submodelSuggestion)}>
                                <TextField
                                    label="Submodel (optional)"
                                    value={submodel}
                                    onChange={onSubmodelChange}
                                    autoComplete="off"
                                    placeholder="e.g. SE"
                                    suggestion={submodelSuggestion}
                                />
                            </div>

                            {errors.make && errors.model && (
                                <InlineError message="Make and Model are required" fieldID="make" />
                            )}
                        </FormLayout>
                    </div>
                </AppProvider>

                <TitleBar
                    title={editingId ? (editingIsCustom ? 'Edit Vehicle' : 'Edit System Vehicle') : 'Add Vehicle'}>
                    <button onClick={handleClose} disabled={submitting} style={{ marginRight: 8 }}>Close</button>
                    <button variant="primary" onClick={onSave} disabled={submitting} style={{ marginRight: 8 }}>Save</button>
                </TitleBar>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal id="delete-vehicles" open={deleteOpen} onHide={() => setDeleteOpen(false)}>
                <AppProvider i18n={enTranslations}>
                    <div style={{ padding: 16 }}>
                        <MiniBanner tone="critical" title="Delete vehicles">
                            <p style={{ marginTop: 8 }}>
                                {deleteInfo.hasStandard && !deleteInfo.hasCustom && (
                                    <>System vehicle data cannot be deleted.</>
                                )}
                                {deleteInfo.hasStandard && deleteInfo.hasCustom && (
                                    <>System vehicle data cannot be deleted. Only user-added vehicles (and their associated fitments) will be deleted. This action cannot be undone.</>
                                )}
                                {!deleteInfo.hasStandard && deleteInfo.hasCustom && (
                                    allResourcesSelected
                                        ? <>You’re about to delete <b>ALL {totalCount}</b> vehicles (and their associated fitments) that match the current filters. This cannot be undone.</>
                                        : <>You’re about to delete <b>{deleteInfo.customCount}</b> selected vehicle(s) (and their associated fitments). This cannot be undone.</>
                                )}
                            </p>
                        </MiniBanner>
                    </div>
                </AppProvider>

                <TitleBar title="Delete vehicles">
                    <button onClick={() => setDeleteOpen(false)} disabled={deleting}>Close</button>
                    <button variant="primary" onClick={confirmDelete} disabled={deleting || !deleteInfo.hasCustom}>Delete</button>
                </TitleBar>
            </Modal>

            <Page
                fullWidth
                title="Vehicles"
                primaryAction={{ content: 'Add Vehicle', onAction: openAdd }}
            >
                <Card>
                    <IndexFilters
                        sortOptions={sortOptions}
                        sortSelected={sortSelected}
                        onSort={handleSort}
                        loading={loadingInitial}
                        tabs={tabs}
                        selected={selected}
                        onSelect={setSelected}
                        queryValue={queryValue}
                        queryPlaceholder="Search vehicles"
                        onQueryChange={handleQueryChange}
                        onQueryClear={handleQueryClear}
                        filters={[
                            {
                                key: 'source',
                                label: 'Data source',
                                shortcut: true,
                                filter: (
                                    <ChoiceList
                                        title="Source"
                                        titleHidden
                                        choices={[
                                            { label: 'System', value: 'System' },
                                            { label: 'User-added', value: 'Custom' },
                                        ]}
                                        selected={selectedSource}
                                        onChange={onSourceChange}
                                        allowMultiple={false}
                                    />
                                ),
                            },
                        ]}
                        appliedFilters={
                            selectedSource.length
                                ? [{ key: 'source', label: `Source: ${selectedSource[0]}`, onRemove: () => setSelectedSource([]) }]
                                : []
                        }
                        onClearAll={onClearAll}
                        mode={mode}
                        setMode={setMode}
                    />

                    <IndexTable
                        condensed={isCondensed}
                        resourceName={{ singular: 'vehicle', plural: 'vehicles' }}
                        itemCount={totalCount}
                        selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                        onSelectionChange={handleSelectionChange}
                        bulkActions={bulkActions}
                        promotedBulkActions={promotedBulkActions}
                        headings={[
                            { title: 'Year' },
                            { title: 'Make' },
                            { title: 'Model' },
                            { title: 'Submodel' },
                            { title: 'Assigned Products' },
                            { title: '' },
                        ]}
                        pagination={{ hasNext, hasPrevious, onNext, onPrevious }}
                    >
                        {(Array.isArray(vehicles) ? vehicles.slice() : []).map((v: any, index: number) => (
                            <IndexTable.Row
                                id={String(v.id)}
                                key={v.id}
                                selected={selectedResources.includes(String(v.id))}
                                position={index}
                            >
                                <IndexTable.Cell><Text as="span">{v.field1}</Text></IndexTable.Cell>
                                <IndexTable.Cell>{v.field2}</IndexTable.Cell>
                                <IndexTable.Cell>{v.field3}</IndexTable.Cell>
                                <IndexTable.Cell>{v.field4}</IndexTable.Cell>

                                {/* Assigned Products as a button (placeholder; no actual nav yet) */}
                                <IndexTable.Cell>
                                    <Button variant="secondary"
                                        onClick={() => {
                                            // TODO: open assigned products page for this vehicle
                                        }}
                                        accessibilityLabel={`Open assigned products for ${v.field1} ${v.field2} ${v.field3} ${v.field4}`}
                                    >
                                        {v.assignedProducts?.toString?.() ?? '0'}
                                    </Button>
                                </IndexTable.Cell>

                                <IndexTable.Cell>
                                    <Button variant="tertiary" onClick={() => onEditClick(v)}>
                                        Edit
                                    </Button>
                                </IndexTable.Cell>
                            </IndexTable.Row>
                        ))}
                    </IndexTable>
                </Card>
            </Page>
        </>
    );
};

export default observer(VehiclesPage);
